import React, {createContext, useContext, useEffect, useState} from 'react';
import {useApolloClient, useLazyQuery} from "@apollo/client";
import {PRODUCT_LISTING} from '../routes/routeQuery';
import {useNavigate} from "react-router-dom";
import {VIEW_MORE_FILTER} from "../components/Category/Filter/FilterTypes/FilterOtherType";
import {useQueryListing} from "../hooks/useQueryListing";
import {SchemaContext} from "./shemaContext";

export const defaultListingData = {
    totalPages: 0,
    totalCount: 0,
    currentPage: 1,
    pageSize: 0,
};
export const defaultSort = {};
export const defaultListingParams = {
    currentPage: 1,
    pageSize: 12,
    sort: defaultSort,
    search: "",
    filters: []
};

const defaultInitialParams = {filters: [], params: {...defaultListingParams}};

const ListingContext = createContext();


export function ListingContextProvider(props) {
    const {getFilterType}=useContext(SchemaContext)
    const queryClient = useApolloClient();
    const [executeQuery] = useLazyQuery(PRODUCT_LISTING, {
        errorPolicy: 'all'
    });
    const [initialListingParams, setInitialListingParams] = useState([])
    const [listingParams, setListingParams] = useState(defaultListingParams);
    const [aggregations, setAggregations] = useState([]);
    const [products, setProducts] = useState([]);
    const [listingData, setListingData] = useState({...defaultListingData});
    const [initialParams, setInitialParams] = useState(defaultInitialParams)
    const [loading, setLoading] = useState(true)
    const navigate = useNavigate();


    const resetListing = (initialParams) => {
        setAggregations([]);
        setListingData(defaultListingData);
        setProducts([])
        setInitialListingParams(initialParams);
    }

    const initListing = (params) => {
        const newListingParams = {
            currentPage: 1,
            pageSize: 12,
            sort: defaultSort,
            search: "",
            filters: []
        };
        params.forEach(param => {
            if (newListingParams.hasOwnProperty(param.key)) {
                newListingParams[param.key] = param.value;
            } else {
                newListingParams.filters = newListingParams.filters.concat([
                    Object.assign({}, param, {inputType: getFilterType(param.key)})
                ])
            }
        })
        return newListingParams;
    }


    // Flaga dla trackowania czy to jest zmiana filtrów (nie pierwszego ładowania)
    const [isFilterChange, setIsFilterChange] = useState(false);

    useEffect(() => {
        // Sprawdzamy czy to nie są defaultowe parametry (porównanie przez JSON)
        const isDefault = JSON.stringify(listingParams) === JSON.stringify(defaultListingParams);
        
        if (!isDefault) {
            console.log('Loading products with params:', listingParams, 'forceRefresh:', isFilterChange);
            // Jeśli to zmiana filtrów, wymuszamy odświeżenie z serwera
            loadProducts(isFilterChange);
        }
    }, [listingParams])
    
    // Osobny useEffect dla resetu flagi
    useEffect(() => {
        if (isFilterChange) {
            setIsFilterChange(false);
        }
    }, [products]) // Reset po załadowaniu produktów

    const buildQuery = (_listingParams) => {
        const uniqueKeys = [];
        const {filters} = _listingParams;
        
        console.log('BuildQuery input filters:', filters);
        
        filters.forEach((filter) => {
            if (!uniqueKeys.includes(filter.key)) {
                uniqueKeys.push(filter.key);
            }
        })
        const finalFilter = {};
        for (const key of uniqueKeys) {
            const paramValues = filters.filter(x => x.key === key);
            const paramCount = +paramValues.length;
            const {inputType} = paramValues.find(x => x); // Get first valid non falsy entry and fetch its input type

            console.log(`Processing filter ${key}:`, {
                paramValues,
                paramCount,
                inputType
            });

            if (inputType === 'FilterRangeTypeInput') {
                // Dla range filtrów, sprawdzamy czy wartość zawiera '-' (rzeczywisty range)
                const value = paramValues.find(x => x).value;
                if (value.includes('-')) {
                    const rangeValue = value.split('-');
                    finalFilter[key] = {from: rangeValue[0], to: rangeValue[1]};
                } else {
                    // Jeśli to pojedyncza wartość, traktujemy jako exact range
                    finalFilter[key] = {from: value, to: value};
                }
                console.log(`Range filter ${key}:`, finalFilter[key]);
            }

            if (inputType === 'FilterMatchTypeInput') {
                // Combine all selected values into one string, because the FilterMatchTypeInput only allows strings
                finalFilter[key] = {match: paramValues.map(x => x.value).join(' ')}; // buildFilter(key, value, inputType)
                console.log(`Match filter ${key}:`, finalFilter[key]);
            }

            if (inputType === 'FilterEqualTypeInput') {
                // Either eq or in based on number of values
                if (paramCount === 1) {
                    finalFilter[key] = {eq: paramValues.find(x => x).value};
                } else {
                    finalFilter[key] = {in: paramValues.map(x => x.value)};
                }
                console.log(`Equal filter ${key}:`, finalFilter[key]);
            }
            
            // Jeśli inputType jest pusty lub nieznany
            if (!inputType || (inputType !== 'FilterRangeTypeInput' && inputType !== 'FilterMatchTypeInput' && inputType !== 'FilterEqualTypeInput')) {
                console.warn(`Unknown or empty inputType for ${key}:`, inputType);
                
                // Specjalne mapowanie dla znanych pól range
                const rangeFields = ['width', 'height', 'price', 'size', 'weight', 'capacity'];
                if (rangeFields.includes(key.toLowerCase())) {
                    const value = paramValues.find(x => x).value;
                    if (value.includes('-')) {
                        const rangeValue = value.split('-');
                        finalFilter[key] = {from: rangeValue[0], to: rangeValue[1]};
                    } else {
                        finalFilter[key] = {from: value, to: value};
                    }
                    console.log(`Fallback range filter ${key}:`, finalFilter[key]);
                } else {
                    // Domyślnie używamy FilterEqualTypeInput
                    if (paramCount === 1) {
                        finalFilter[key] = {eq: paramValues.find(x => x).value};
                    } else {
                        finalFilter[key] = {in: paramValues.map(x => x.value)};
                    }
                    console.log(`Default equal filter ${key}:`, finalFilter[key]);
                }
            }
        }
        _listingParams.currentPage = Math.max(_listingParams.currentPage, 1);
        const queryParams = Object.assign(
                {},
                _listingParams,
                {filter: finalFilter},
            )
        ;
        
        console.log('BuildQuery result:', queryParams);
        return queryParams;
    }

    const buildUrl = () => {
        const queryStringParams = new URLSearchParams();
        if (listingParams.search) {
            queryStringParams.append('q', listingParams.search);
        }
        listingParams.filters.forEach(param => {
            const _f = [...initialListingParams].find((_param) => {
                return _param.key == param.key && _param.value == param.value
            });
            if (!_f) {
                queryStringParams.append(param.key, param.value);
            }
        })
        if (listingParams.currentPage > 1) {
            queryStringParams.append('p', listingParams.currentPage);
        }
        if (listingParams.pageSize != 12) {
            queryStringParams.append('page-size', listingParams.pageSize);
        }
        if (listingParams.sort) {
            for (const [key, value] of Object.entries(listingParams.sort)) {
                queryStringParams.append('product_list_order', key);
                queryStringParams.append('product_list_dir', value);
            }
        }
        initialListingParams.forEach((filter) => {
            if (queryStringParams.has(filter.key) && queryStringParams.get(filter.key) == filter.value) {
                queryStringParams.delete(filter.key)
            }
        })
        navigate(`?${queryStringParams.toString()}`)
    }
    const loadProducts = async (forceRefresh = false) => {
        try {
            setLoading(true)
            
            // Sprawdzamy czy query client jest gotowy
            if (!queryClient) {
                console.error('Query client not available');
                setLoading(false);
                return;
            }
            
            // Wybór fetch policy w zależności od tego czy wymuszamy odświeżenie
            const fetchPolicy = forceRefresh ? 'network-only' : 'cache-and-network';
            const queryVariables = buildQuery(listingParams);
            
            console.log('LoadProducts called with:', {
                forceRefresh,
                fetchPolicy,
                variables: queryVariables,
                listingParams: listingParams
            });

            // Sprawdźmy czy query jest poprawnie zdefiniowany
            console.log('PRODUCT_LISTING query:', PRODUCT_LISTING);
            console.log('Query string:', PRODUCT_LISTING.loc?.source?.body?.substring(0, 200));

            const result = await executeQuery({
                variables: queryVariables,
                fetchPolicy: fetchPolicy
            });
            
            console.log('executeQuery result:', result);
            
            if (!result) {
                console.error('No result from executeQuery');
                setLoading(false);
                return;
            }
            
            const data = result.data;
            console.log('Query result data:', data);
            
            if (data && data.products) {
                const listData = {
                    totalPages: Math.ceil(data.products.total_count / data.products.page_info.page_size),
                    totalCount: data.products.total_count,
                    currentPage: data.products.page_info.current_page,
                    pageSize: data.products.page_info.page_size,
                };
                const out = Object.assign({}, listingData, listData);
                setListingData(out);
                setProducts(data.products.items || []); // Zawsze ustawiamy tablicę
                
                if (aggregations.length == 0) {
                    setAggregations(data.products.aggregations?.filter((aggregation)=>{
                        return aggregation['attribute_code']!='category_id'
                    }) || []);
                }
                buildUrl()
                
                console.log('Products set:', data.products.items?.length || 0);
            } else {
                console.error('No products data in response');
                setProducts([]);
            }
            setLoading(false)
        } catch (error) {
            console.error('Błąd loadProducts:', error);
            console.error('Error stack:', error.stack);
            console.error('Error details:', {
                message: error.message,
                graphQLErrors: error.graphQLErrors,
                networkError: error.networkError,
                extraInfo: error.extraInfo
            });
            setLoading(false);
            // Ustawiamy pustą listę przy błędzie
            setProducts([]);
        }
    }
    const loadFilters = async (attribute_code) => {
        // Tworzymy query tylko z parametrami kategorii, bez dodatkowych filtrów
        const baseParams = initialListingParams.filter(param => 
            param.key === 'category_uid' || 
            param.key === 'search' || 
            param.key === 'pageSize' || 
            param.key === 'currentPage'
        );
        
        const {filter, search} = buildQuery(initListing(baseParams));
        
        console.log('LoadFilters for', attribute_code, 'with base params:', baseParams);
        
        return queryClient.query({
            query: VIEW_MORE_FILTER,
            variables: {
                filter: filter,
                search: search,
                filterName: attribute_code
            }
        }).then((data) => {
            const items = [];
            data.data.viewMoreFilter.aggregations.forEach((data) => {
                data.options.forEach((item) => {
                    items.push(item)
                })
            })
            console.log('LoadFilters result for', attribute_code, ':', items.length, 'items');
            return items;
        });
    }

    // Funkcja do ustawiania parametrów z wymuszeniem odświeżenia (dla filtrów)
    const setListingParamsWithRefresh = (newParams) => {
        setIsFilterChange(true);
        setListingParams(newParams);
    };

    const data = {
        aggregations: aggregations,
        products: products,
        listingData: listingData,
        listingParams: listingParams,
        setListingParams: setListingParams,
        setListingParamsWithRefresh: setListingParamsWithRefresh,
        loadProducts: loadProducts,
        initialParams: initialParams,
        setInitialParams: setInitialParams,
        initialListingParams: initialListingParams,
        setInitialListingParams: setInitialListingParams,
        initListing: initListing,
        loadFilters: loadFilters,
        resetListing: resetListing,
        loading: loading
    };

    return (
        <ListingContext.Provider value={data}>
            {props.children}
        </ListingContext.Provider>
    );
}


export function useListingContext() {
    return useContext(ListingContext);
};
